import { Phone, Mail, MapPin, ChevronRight, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import logoColor2 from "@/assets/logo-color2.png";

const cities = ["Versailles", "Saint-Germain-en-Laye", "Poissy", "Mantes-la-Jolie", "Sartrouville", "Chatou", "Le Chesnay", "Rambouillet", "Conflans-Sainte-Honorine", "Houilles"];

const Footer = () => (
  <footer className="relative overflow-hidden" style={{ background: "var(--gradient-navy)" }}>
    {/* Decorative top border */}
    <div className="h-1 w-full" style={{ background: "var(--gradient-tile)" }} />

    {/* Main footer content */}
    <div className="container-narrow mx-auto px-4 pt-14 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">

        {/* Logo & description — 3 cols */}
        <div className="md:col-span-3">
          <Link to="/" className="inline-block mb-4">
            <img src={logoColor2} alt="HNJR Couverture" className="max-h-24 w-auto object-contain" width={218} height={96} />
          </Link>
          <p className="text-sm leading-relaxed" style={{ color: "hsl(0 0% 100% / 0.5)" }}>
            Entreprise familiale depuis 4 générations — 79 ans d'expertise en couverture, charpente, zinguerie et isolation dans les Yvelines.
          </p>
          {/* CTA */}
          <Link
            to="/devis"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-md transition-all duration-300 hover:brightness-110"
            style={{ background: "var(--gradient-tile)", color: "white" }}
          >
            Devis gratuit <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Services — 2 cols */}
        <div className="md:col-span-2">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-4 text-primary">
            Nos services
          </h3>
          <ul className="space-y-2.5 text-sm">
            {[
              { label: "Couverture", href: "/services#couverture" },
              { label: "Charpente", href: "/services#charpente" },
              { label: "Zinguerie", href: "/services#zinguerie" },
              { label: "Isolation", href: "/services#isolation" },
              { label: "Réalisations", href: "/realisations", isLink: true },
            ].map((item) => (
              <li key={item.label}>
                {item.isLink ? (
                  <Link to={item.href} className="inline-flex items-center gap-1.5 text-white/60 transition-colors hover:!text-gold-light">
                    <ChevronRight className="w-3 h-3 text-primary" />
                    {item.label}
                  </Link>
                ) : (
                  <a href={item.href} className="inline-flex items-center gap-1.5 text-white/60 transition-colors hover:!text-gold-light">
                    <ChevronRight className="w-3 h-3 text-primary" />
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Zones — 4 cols */}
        <div className="md:col-span-4">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-4 text-primary">
            Zones d'intervention
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {cities.map((city) => (
              <span key={city} className="inline-flex items-center gap-1.5" style={{ color: "hsl(0 0% 100% / 0.6)" }}>
                <MapPin className="w-3 h-3 text-primary flex-shrink-0" />
                {city}
              </span>
            ))}
          </div>
        </div>

        {/* Contact — 3 cols */}
        <div className="md:col-span-3">
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider mb-4 text-primary">
            Contact
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2.5" style={{ color: "hsl(0 0% 100% / 0.6)" }}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "hsl(var(--gold) / 0.15)" }}>
                <Phone className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="flex items-center gap-1.5 flex-wrap">
                <a href="tel:+33189625959" className="transition-colors hover:text-gold-light">01 89 62 59 59</a>
                <span className="opacity-40">|</span>
                <a href="tel:+33612951944" className="transition-colors hover:text-gold-light">06 12 95 19 44</a>
              </span>
            </div>
            <a href="mailto:contact@hnjr-couverture.fr" className="flex items-center gap-2.5 transition-colors hover:text-gold-light" style={{ color: "hsl(0 0% 100% / 0.6)" }}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "hsl(var(--gold) / 0.15)" }}>
                <Mail className="w-3.5 h-3.5 text-primary" />
              </div>
              contact@hnjr-couverture.fr
            </a>
            <div className="flex items-center gap-2.5" style={{ color: "hsl(0 0% 100% / 0.6)" }}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "hsl(var(--gold) / 0.15)" }}>
                <MapPin className="w-3.5 h-3.5 text-primary" />
              </div>
              Yvelines (78)
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 text-gold-light font-medium hover:underline transition-colors mt-1"
            >
              Formulaire de contact <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom bar */}
    <div style={{ borderTop: "1px solid hsl(213 26% 26%)" }}>
      <div className="container-narrow mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs" style={{ color: "hsl(0 0% 100% / 0.35)" }}>
        <span>© {new Date().getFullYear()} HNJR Couverture. Tous droits réservés.</span>
        <span>
          Created with 🧡 by{" "}
          <a href="https://www.mycompartner.fr" target="_blank" rel="noopener noreferrer" className="hover:text-gold-light transition-colors font-medium">
            myCOM Partner
          </a>
        </span>
        <div className="flex gap-4">
          <Link to="/mentions-legales" className="hover:text-gold-light transition-colors">Mentions légales</Link>
          <Link to="/politique-de-confidentialite" className="hover:text-gold-light transition-colors">Confidentialité</Link>
          <button onClick={() => window.dispatchEvent(new Event("open-cookie-banner"))} className="hover:text-gold-light transition-colors">Cookies</button>
          <Link to="/admin" className="hover:text-gold-light transition-colors">Admin</Link>
        </div>
      </div>
    </div>

    {/* Back to top */}
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="absolute bottom-20 right-6 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:brightness-110"
      style={{ background: "var(--gradient-tile)", color: "white" }}
      aria-label="Retour en haut"
    >
      <ArrowUp className="w-4 h-4" />
    </button>
  </footer>
);

export default Footer;
